// Loaded through CMS config by next.config.ts; its import chain must use relative paths.
import type { Brand } from '../cms/brandConstants';
import { requireBrand } from '../cms/requireBrand';
import type { ConfigurationOperationsPolicy } from './configurationOperations';

/**
 * Semantic capabilities consumed by shared code.
 *
 * Add a trait when brands need different behavior inside the same component or
 * workflow. Shared code must ask for the capability it needs instead of
 * branching on a brand name.
 */
export type SitemapProductType = 'watch' | 'strap' | 'eyewear';

/**
 * How hreflang, robots.txt, and sitemaps choose "primary" locale slugs.
 * Breitling keeps the legacy intersection of HREFLANG_COUNTRY_MAP with all
 * configured locales; Gallet adds live-market fallback for English-only markets.
 */
export type PrimaryLocalesPolicy = 'hreflang-map-intersection' | 'live-market-fallback';

export interface BrandTraits {
  primaryLocalesPolicy: PrimaryLocalesPolicy;
  /** Where the proxy sends a visitor with no locale preference and no served market. */
  fallbackLocale?: string;
  /**
   * Whether the brand has an Algolia **product** index (the STORE index used by
   * boutiques is separate and unaffected). False for UG, which is Saleor-only.
   *
   * Product paths must check this rather than relying on the index being
   * absent: the PDP availability gate reads "no hit" as "not available", so a
   * brand without an index silently 404s every product page.
   */
  usesAlgoliaProductIndex: boolean;
  /**
   * Product types emitted in locale sitemaps. Empty means CMS/PLP URLs only.
   * Add a type here (and a mapper in the sitemap product registry) to extend.
   */
  sitemapProductTypes: readonly SitemapProductType[];
  usesAlgoliaContactCentre: boolean;
  /**
   * Whether `/ew/card` renders the warranty card in place instead of validating
   * and redirecting into My Account. UG has no My Account under
   * `FEATURE_ECOM=false`, so the redirect target 404s there (BEC-24473).
   */
  rendersWarrantyCardInPlace: boolean;
  /** Salesforce warranty-extension feature policy (page vs session proxy are separate). */
  warrantyExtension: {
    /** Whether `/extend-ewarranty` is served (embeds the widget). */
    hasExtensionPage: boolean;
    /** Whether `/api/ewarranty/session` may mint a server-side session token. */
    supportsSessionProxy: boolean;
  };
  hasCustomerAccounts: boolean;
  hasWishlistCompare: boolean;
  showsWishlistSku: boolean;
  hasSiteSearch: boolean;
  servesStoresPage: boolean;
  /**
   * Catalog-mode PDP can offer the Gallet-style availability-request CTA when
   * Configuration App enables `AVAILABILITY_REQUEST` (BEC-25290).
   */
  catalogAvailabilityRequestCta: boolean;
  /**
   * CMS video behavior consumed by shared renderers. Keep brand identity out of
   * components: adding a brand should require one policy entry here, not another
   * conditional at every rendering site.
   */
  cmsVideo: {
    /** Attach sources only when offscreen media is approaching the viewport. */
    deferOffscreenLoading: boolean;
    /** Pause outside the viewport and resume when visible. */
    viewportPlayback: boolean;
    /** Render authored YouTube URLs as an embed instead of the video asset. */
    youtubeEmbeds: boolean;
    hero: {
      /** Let the authored play option determine the expected initial state. */
      honorPlayOption: boolean;
      /** Place hero CTAs outside the media frame. */
      ctaOutsideMedia: boolean;
      /** Render the centered play overlay when the hero is paused. */
      showPlayOverlay: boolean;
    };
    paragraph: {
      /** Forward authored play options for paragraph-style media cards. */
      honorPlayOption: boolean;
      /** Render the in-player sound and play/pause controls. */
      showToggles: boolean;
      /** Render the centered play overlay when playback is paused. */
      showPlayOverlay: boolean;
    };
  };
  pdp: {
    stickyPurchaseBar: boolean;
    tracksRecentlyViewed: boolean;
    structuredDataIncludesOffer: boolean;
  };
  formsTitle: {
    /** Legacy Breitling "Mrs" maps to "Ms" on forms and outbound payloads (BEC-25439). */
    mapsIncomingMrsToMs: boolean;
    /** Whether salutation pickers include "Mrs" (Gallet omits it; see mapsIncomingMrsToMs). */
    includesMrsInTitleOptions: boolean;
    /** IT/ES offer Ms even when Mrs is the local default (Gallet always shows Ms). */
    offersMsInMrsMarkets: boolean;
  };
  layout: {
    /** CMS headerType "simple" maps to the brand simple header (not UG). */
    supportsSimpleHeader: boolean;
  };
  configuration: {
    /** Which Configuration App operations this brand's deployment may call. */
    supportedOperations: ConfigurationOperationsPolicy;
  };
}

const UG_CONFIGURATION_OPERATIONS = [
  'GetModuleActivations',
  'GetSupportedCountriesByBrand',
] as const satisfies ConfigurationOperationsPolicy;

/**
 * Exhaustive capability map. Adding a brand to the Brand union must require a
 * corresponding entry here.
 */
const BRAND_TRAITS_BY_BRAND: Record<Brand, BrandTraits> = {
  gallet: {
    primaryLocalesPolicy: 'live-market-fallback',
    usesAlgoliaProductIndex: true,
    sitemapProductTypes: ['watch', 'strap'],
    usesAlgoliaContactCentre: true,
    rendersWarrantyCardInPlace: false,
    warrantyExtension: { hasExtensionPage: false, supportsSessionProxy: false },
    hasCustomerAccounts: true,
    hasWishlistCompare: false,
    showsWishlistSku: false,
    hasSiteSearch: true,
    servesStoresPage: false,
    catalogAvailabilityRequestCta: true,
    cmsVideo: {
      deferOffscreenLoading: true,
      viewportPlayback: true,
      youtubeEmbeds: false,
      hero: {
        honorPlayOption: false,
        ctaOutsideMedia: false,
        showPlayOverlay: false,
      },
      paragraph: {
        honorPlayOption: false,
        showToggles: true,
        showPlayOverlay: false,
      },
    },
    pdp: {
      stickyPurchaseBar: true,
      tracksRecentlyViewed: true,
      structuredDataIncludesOffer: true,
    },
    formsTitle: {
      mapsIncomingMrsToMs: true,
      includesMrsInTitleOptions: false,
      offersMsInMrsMarkets: true,
    },
    layout: {
      supportsSimpleHeader: true,
    },
    configuration: {
      supportedOperations: 'all',
    },
  },
  breitling: {
    primaryLocalesPolicy: 'hreflang-map-intersection',
    usesAlgoliaProductIndex: true,
    sitemapProductTypes: [],
    usesAlgoliaContactCentre: true,
    rendersWarrantyCardInPlace: false,
    warrantyExtension: { hasExtensionPage: false, supportsSessionProxy: false },
    hasCustomerAccounts: true,
    hasWishlistCompare: true,
    showsWishlistSku: true,
    hasSiteSearch: true,
    servesStoresPage: false,
    catalogAvailabilityRequestCta: false,
    cmsVideo: {
      deferOffscreenLoading: false,
      viewportPlayback: false,
      youtubeEmbeds: false,
      hero: {
        honorPlayOption: false,
        ctaOutsideMedia: false,
        showPlayOverlay: false,
      },
      paragraph: {
        honorPlayOption: false,
        showToggles: false,
        showPlayOverlay: false,
      },
    },
    pdp: {
      stickyPurchaseBar: false,
      tracksRecentlyViewed: true,
      structuredDataIncludesOffer: true,
    },
    formsTitle: {
      mapsIncomingMrsToMs: false,
      includesMrsInTitleOptions: true,
      offersMsInMrsMarkets: false,
    },
    layout: {
      supportsSimpleHeader: true,
    },
    configuration: {
      supportedOperations: 'all',
    },
  },
  ug: {
    primaryLocalesPolicy: 'hreflang-map-intersection',
    fallbackLocale: 'ch-en',
    usesAlgoliaProductIndex: false,
    sitemapProductTypes: [],
    usesAlgoliaContactCentre: false,
    rendersWarrantyCardInPlace: true,
    warrantyExtension: { hasExtensionPage: true, supportsSessionProxy: true },
    hasCustomerAccounts: true,
    hasWishlistCompare: false,
    showsWishlistSku: true,
    hasSiteSearch: false,
    servesStoresPage: true,
    catalogAvailabilityRequestCta: false,
    cmsVideo: {
      deferOffscreenLoading: false,
      viewportPlayback: true,
      youtubeEmbeds: true,
      hero: {
        honorPlayOption: true,
        ctaOutsideMedia: true,
        showPlayOverlay: true,
      },
      paragraph: {
        honorPlayOption: true,
        showToggles: true,
        showPlayOverlay: true,
      },
    },
    pdp: {
      stickyPurchaseBar: false,
      tracksRecentlyViewed: false,
      structuredDataIncludesOffer: false,
    },
    formsTitle: {
      mapsIncomingMrsToMs: false,
      includesMrsInTitleOptions: true,
      offersMsInMrsMarkets: false,
    },
    layout: {
      supportsSimpleHeader: false,
    },
    configuration: {
      supportedOperations: UG_CONFIGURATION_OPERATIONS,
    },
  },
};

export function getBrandTraits(brand: Brand = requireBrand('application runtime')): BrandTraits {
  return BRAND_TRAITS_BY_BRAND[brand];
}

export const BRAND_TRAITS: BrandTraits = getBrandTraits();
